import Papa from "papaparse";
import { BaseFermentationParser, } from "./parser-interface";
/**
 * DASGIP parser implementing the new FermentationTimeseriesParser interface
 * Updated for Phase 3: Parser Migration
 */
export class DasGipParser extends BaseFermentationParser {
    equipmentType = "DASGIP";
    supportedFormats = [".csv", ".txt"];
    argsSchema = {
        ignoreMethanolZeroSetpoint: {
            type: "boolean",
            description: "If checked, the fermentation will not be considered ended when methanol setpoint reaches zero. This is useful when the fermentation continues after methanol feeding has stopped.",
            required: false,
            default: true,
        },
    };
    fileContents;
    constructor(fileContents) {
        super();
        this.fileContents = fileContents;
    }
    decodeFile(buffer) {
        const decoder = new TextDecoder("utf-8");
        // Normalize line endings in a single pass. Chaining two replaces allocates
        // two additional full-file strings, which is significant for large exports.
        return decoder.decode(buffer).replace(/\r\n?/g, "\n");
    }
    async validateFile(buffer) {
        try {
            const textContents = this.decodeFile(buffer);
            // Check for required sections
            const sections = this.getSectionNames(textContents);
            const errors = [];
            const warnings = [];
            if (!sections.includes("ProjectInfo")) {
                errors.push("Missing required ProjectInfo section");
            }
            const trackSections = sections.filter((s) => s.startsWith("TrackData"));
            if (trackSections.length === 0) {
                errors.push("No TrackData sections found");
            }
            // Validate data structure
            try {
                await this.parseProjectInfo(textContents);
            }
            catch (error) {
                errors.push(`Invalid ProjectInfo section: ${error}`);
            }
            return {
                isValid: errors.length === 0,
                errors,
                warnings,
            };
        }
        catch (error) {
            return {
                isValid: false,
                errors: [`File validation failed: ${error}`],
            };
        }
    }
    async extractMetadata(buffer) {
        const textContents = this.decodeFile(buffer);
        const projectInfo = await this.parseProjectInfo(textContents);
        const tankNumbers = this.getTankNumbers(textContents);
        const lastTimestampS = await this.getLastTimestampS(textContents);
        const durationSeconds = lastTimestampS ?? undefined;
        const endTime = lastTimestampS !== null
            ? new Date(projectInfo.startTime.getTime() + lastTimestampS * 1000)
            : undefined;
        return {
            equipmentType: this.equipmentType,
            startTime: projectInfo.startTime,
            endTime,
            durationSeconds,
            projectName: projectInfo.projectName,
            tankNumbers,
        };
    }
    /**
     * Elapsed seconds (EFT) of the last data point across all tracks, using the
     * same inoculation-time-based timestamp as parseData. Returns null if no
     * track has a parseable inoculation time.
     */
    async getLastTimestampS(textContents) {
        const trackSections = this.getSectionNames(textContents).filter((section) => section.startsWith("TrackData") && section.length > 9);
        const excelEpoch = new Date("1899-12-30 00:00:00").getTime();
        let maxTimestampS = null;
        for (const section of trackSections) {
            const track = parseInt(section.slice(9));
            const inoculationTime = await this.findLastInoculationTime(textContents, track);
            if (!inoculationTime)
                continue;
            const timestamp_s = (new Date(inoculationTime).getTime() - excelEpoch) / 1000;
            if (!isNaN(timestamp_s)) {
                if (maxTimestampS === null || timestamp_s > maxTimestampS) {
                    maxTimestampS = timestamp_s;
                }
            }
        }
        return maxTimestampS;
    }
    /**
     * Inoculation time of the last row of a track that carries one.
     *
     * Scans backwards over the raw section text and parses one line at a time,
     * because the caller only ever needs the final value. Parsing the whole
     * track to read it costs ~300 MB on a 30 MB export and was the dominant
     * allocation in the upload path (see the 2026-07-29 outage). Falls back to a
     * full parse if the bounded scan finds nothing, preserving the original
     * semantics for pathological files.
     */
    async findLastInoculationTime(textContents, track) {
        const key = `Unit ${track}.InoculationTime []`;
        const bounds = this.locateSection(textContents, `TrackData${track}`);
        if (bounds) {
            const { start: contentStart, end } = bounds;
            // The header is the first non-empty line of the section.
            let headerStart = contentStart;
            while (headerStart < end && textContents[headerStart] === "\n")
                headerStart++;
            const headerEnd = textContents.indexOf("\n", headerStart);
            if (headerEnd >= 0 && headerEnd < end) {
                const header = textContents.slice(headerStart, headerEnd);
                // Walk back from the end of the section, one line at a time. In
                // practice the final line carries a value, so this parses once.
                let lineEnd = end;
                for (let scanned = 0; scanned < 1000; scanned++) {
                    if (lineEnd <= headerEnd)
                        break;
                    const lineStart = textContents.lastIndexOf("\n", lineEnd - 1) + 1;
                    if (lineStart <= headerEnd)
                        break;
                    const line = textContents.slice(lineStart, lineEnd).trim();
                    lineEnd = lineStart - 1;
                    if (!line)
                        continue;
                    const [row] = await this.parseCsv(`${header}\n${line}`);
                    const value = row?.[key];
                    if (value)
                        return value;
                }
            }
        }
        const rows = await this.parseTrackData(textContents, track);
        for (let i = rows.length - 1; i >= 0; i--) {
            const value = rows[i][key];
            if (value)
                return value;
        }
        return undefined;
    }
    async parseData(buffer, fermentations, tankIdToFermIdMap, args, additionTotals) {
        return this.collectRecordings(this.parseRecordStream(buffer, fermentations, tankIdToFermIdMap, args, additionTotals));
    }
    /**
     * Streaming implementation. Each track is one fermentation and tracks are
     * processed in order, so records for a fermentation are contiguous.
     *
     * Rows are pulled a window at a time rather than parsing the whole track, so
     * peak memory is bounded by the window rather than by row count. The
     * end-of-fermentation condition is forward-only stateful, which is why this
     * format can be streamed at all.
     *
     * Note this yields nothing for a mapped track whose rows all lack an
     * inoculation time, where the previous implementation returned a recording
     * with an empty `records` array. Callers create a recording row per
     * fermentation that actually produced data, which is the more useful
     * behaviour; no test depended on the old shape.
     */
    async *parseRecordStream(buffer, fermentations, tankIdToFermIdMap, args, _additionTotals) {
        const textContents = this.decodeFile(buffer);
        const ignoreMethanolZeroSetpoint = args?.ignoreMethanolZeroSetpoint ?? false;
        const trackSections = this.getSectionNames(textContents).filter((section) => section.startsWith("TrackData") && section.length > 9);
        const excelEpoch = new Date("1899-12-30 00:00:00").getTime();
        for (const section of trackSections) {
            const track = parseInt(section.slice(9));
            // Map track to fermentation ID
            const fermentationId = tankIdToFermIdMap[track];
            if (!fermentationId) {
                console.warn(`No fermentation mapped for track ${track}, skipping`);
                continue;
            }
            // Find the corresponding fermentation object to validate
            const fermentation = fermentations.find((f) => f.id === fermentationId);
            if (!fermentation) {
                console.warn(`Fermentation ${fermentationId} not found for track ${track}, skipping`);
                continue;
            }
            const bounds = this.locateSection(textContents, `TrackData${track}`);
            if (!bounds)
                continue;
            let hadMethanolSetPointValue = false;
            const rows = this.iterateDelimitedRows(textContents, bounds.start, bounds.end, (chunk) => this.parseCsv(chunk));
            for await (const row of rows) {
                if (!row[`Unit ${track}.InoculationTime []`]) {
                    continue;
                }
                const methanolSetPointValue = this.toPerMin(row[`Unit ${track}.FC1.SP [mL/h]`]);
                if (methanolSetPointValue && methanolSetPointValue > 0) {
                    hadMethanolSetPointValue = true;
                }
                if (hadMethanolSetPointValue &&
                    !ignoreMethanolZeroSetpoint &&
                    methanolSetPointValue === 0) {
                    console.log("fermentation has ended", row);
                    // Fermentation has ended
                    break;
                }
                // Calculate timestamp (EFT in seconds)
                const inoculationTimeMs = new Date(row[`Unit ${track}.InoculationTime []`]).getTime();
                const timestamp_s = (inoculationTimeMs - excelEpoch) / 1000;
                // Map attributes to standardized format
                const rawData = this.createRawDataForTrack(track, row);
                const attributes = this.mapAttributes(rawData);
                yield {
                    fermentationId,
                    timestamp_s,
                    attributes,
                };
            }
        }
    }
    // Public methods for direct access (used by tests)
    getSectionNames(fileContents) {
        const content = fileContents || this.fileContents;
        if (!content) {
            throw new Error("No file contents available");
        }
        const regex = new RegExp(/^"\[[a-zA-Z0-9]+\]"$/gm);
        const matches = content.match(regex);
        if (!matches) {
            throw new Error("Could not find sections in file");
        }
        return matches.map((match) => match.slice(2, match.length - 2));
    }
    getTankNumbers(fileContents) {
        const content = fileContents || this.fileContents;
        if (!content) {
            throw new Error("No file contents available");
        }
        const sections = this.getSectionNames(content);
        return sections
            .filter((section) => section.startsWith("TrackData") && section.length > 9)
            .map((section) => parseInt(section.slice(9)));
    }
    /**
     * Byte range of a section's content, exclusive of its `"[Name]"` marker.
     * Returns indices rather than a slice so callers can scan large sections
     * without copying them.
     */
    locateSection(fileContents, sectionName) {
        const searchStr = `"[${sectionName}]"`;
        const searchIndex = fileContents.indexOf(searchStr);
        if (searchIndex < 0)
            return null;
        const start = searchIndex + searchStr.length;
        const end = fileContents.indexOf("\n\n", start);
        return { start, end: end < 0 ? fileContents.length : end };
    }
    async parseSection(fileContents, sectionName) {
        const searchStr = `"[${sectionName}]"`;
        const searchIndex = fileContents.indexOf(searchStr);
        if (searchIndex < 0) {
            throw new Error(`Could not find section ${sectionName} in file`);
        }
        const startIndex = searchIndex + searchStr.length;
        const endIndex = fileContents.indexOf("\n\n", startIndex);
        const section = fileContents.slice(startIndex, endIndex).trim();
        return this.parseCsv(section);
    }
    async parseTrackData(fileContents, trackNumber) {
        return this.parseSection(fileContents, `TrackData${trackNumber}`);
    }
    async parseProjectInfo(fileContents) {
        const content = fileContents || this.fileContents;
        if (!content) {
            throw new Error("No file contents available");
        }
        const [projectInfo] = await this.parseSection(content, "ProjectInfo");
        return {
            projectName: projectInfo["ProjName"],
            startTime: new Date(projectInfo["StartTimestamp"]),
        };
    }
    getAttributeMapping() {
        return {
            // pH attributes
            ph_value: "ph_pv",
            ph_setpoint: "ph_sp",
            ph_output_pct: "ph_out",
            // Temperature attributes
            temperature_value: "temp_pv",
            temperature_setpoint: "temp_sp",
            temperature_output_pct: "temp_out",
            // Dissolved oxygen attributes
            do_value_pct: "do_pv",
            do_setpoint_pct: "do_sp",
            do_output_pct: "do_out",
            // Agitation attributes
            agitation_value_rpm: "agit_pv",
            agitation_setpoint_rpm: "agit_sp",
            // Air flow attributes
            air_flow_value_l_min: "airflow_pv",
            air_flow_setpoint_l_min: "airflow_sp",
            // Feed attributes
            antifoam_value_g_min: "antifoam_feed_rate_pv",
            antifoam_setpoint_g_min: "antifoam_feed_rate_sp",
            antifoam_total_g: "antifoam_feed_total", // Map legacy total_g to new added_g naming
            base_value_g_min: "base_feed_rate_pv",
            base_setpoint_g_min: "base_feed_rate_sp",
            base_total_g: "base_feed_total", // Map legacy total_g to new added_g naming
            methanol_value_g_min: "methanol_feed_rate_pv",
            methanol_setpoint_g_min: "methanol_feed_rate_sp",
            methanol_total_g: "methanol_feed_total", // Map legacy total_g to new added_g naming
            nutrients_value_g_min: "nutrient_feed_rate_pv",
            nutrients_setpoint_g_min: "nutrient_feed_rate_sp",
            nutrients_total_g: "nutrient_feed_total", // Map legacy total_g to new added_g naming
            // Level sensor
            level_sensor: "level_pv",
        };
    }
    normalizeColumnName(name) {
        return name
            .toLowerCase()
            .replace(/[°\u00B0\u00BA\uFFFD]/g, "") // remove degree and replacement chars
            .replace(/[^\x20-\x7E]/g, "") // remove non-ASCII
            .replace(/\s+/g, " ") // normalize whitespace
            .trim();
    }
    getRowKey(row, targetColumn) {
        const normalizedTarget = this.normalizeColumnName(targetColumn);
        for (const key of Object.keys(row)) {
            if (this.normalizeColumnName(key) === normalizedTarget) {
                return key;
            }
        }
        return null;
    }
    getRowNumber(row, targetColumn) {
        const key = this.getRowKey(row, targetColumn);
        const value = key ? row[key] : null;
        return this.toNumber(value);
    }
    createRawDataForTrack(track, row) {
        return {
            ph_value: this.toNumber(row[`Unit ${track}.pH${track}.PV [pH]`]),
            ph_setpoint: this.toNumber(row[`Unit ${track}.pH${track}.SP [pH]`]),
            ph_output_pct: this.toNumber(row[`Unit ${track}.pH${track}.Out [%]`]),
            temperature_value: this.getRowNumber(row, `Unit ${track}.T${track}.PV [°C]`),
            temperature_setpoint: this.getRowNumber(row, `Unit ${track}.T${track}.SP [°C]`),
            temperature_output_pct: this.toNumber(row[`Unit ${track}.T${track}.Out [%]`]),
            do_value_pct: this.toNumber(row[`Unit ${track}.DO${track}.PV [%DO]`]),
            do_setpoint_pct: this.toNumber(row[`Unit ${track}.DO${track}.SP [%DO]`]),
            do_output_pct: this.toNumber(row[`Unit ${track}.DO${track}.Out [%]`]),
            agitation_value_rpm: this.toNumber(row[`Unit ${track}.N${track}.PV [rpm]`]),
            agitation_setpoint_rpm: this.toNumber(row[`Unit ${track}.N${track}.SP [rpm]`]),
            air_flow_value_l_min: this.toPerMin(row[`Unit ${track}.FAir${track}.PV [sL/h]`]),
            air_flow_setpoint_l_min: this.toPerMin(row[`Unit ${track}.FAir${track}.SP [sL/h]`]),
            antifoam_value_g_min: this.toPerMin(row[`Unit ${track}.FA${track}.PV [mL/h]`]),
            antifoam_setpoint_g_min: this.toPerMin(row[`Unit ${track}.FA${track}.SP [mL/h]`]),
            antifoam_total_g: this.toNumber(row[`Unit ${track}.VA${track}.PV [mL]`]),
            base_value_g_min: this.toPerMin(row[`Unit ${track}.FB${track}.PV [mL/h]`]),
            base_setpoint_g_min: this.toPerMin(row[`Unit ${track}.FB${track}.SP [mL/h]`]),
            base_total_g: this.toNumber(row[`Unit ${track}.VB${track}.PV [mL]`]),
            methanol_value_g_min: this.toPerMin(row[`Unit ${track}.FC${track}.PV [mL/h]`]),
            methanol_setpoint_g_min: this.toPerMin(row[`Unit ${track}.FC1.SP [mL/h]`]),
            methanol_total_g: this.toNumber(row[`Unit ${track}.VC${track}.PV [mL]`]),
            nutrients_value_g_min: this.toPerMin(row[`Unit ${track}.FD${track}.PV [mL/h]`]),
            nutrients_setpoint_g_min: this.toPerMin(row[`Unit ${track}.FD${track}.SP [mL/h]`]),
            nutrients_total_g: this.toNumber(row[`Unit ${track}.VD${track}.PV [mL]`]),
            level_sensor: this.getRowNumber(row, `Unit ${track}.Lvl${track}.PV [µS]`),
        };
    }
    async parseCsv(content) {
        return new Promise((resolve, reject) => Papa.parse(content, {
            delimiter: ";",
            header: true,
            complete: (result) => {
                resolve(result.data);
            },
            error: reject,
        }));
    }
}
//# sourceMappingURL=dasgip.js.map